import { useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';

interface UploadAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File, name: string) => void;
  /** When true, upload is globally busy */
  isBusy?: boolean;
}

export function UploadAssetModal({ isOpen, onClose, onConfirm, isBusy }: UploadAssetModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [tab, setTab] = useState<'device' | 'url' | 'drive'>('device');
  const [error, setError] = useState('');
  const [webUrl, setWebUrl] = useState('');
  const [webName, setWebName] = useState('');
  const [webLoading, setWebLoading] = useState(false);

  function nameFromUrl(url: string): string {
    try {
      const path = new URL(url).pathname;
      return path.split('/').filter(Boolean).pop() ?? 'asset';
    } catch {
      return 'asset';
    }
  }

  function isSupportedMedia(blob: Blob, contentType: string): boolean {
    const mime = (blob.type || contentType).split(';')[0].toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
  }

  const resetForm = () => {
    setFile(null);
    setName('');
    setTab('device');
    setError('');
    setWebUrl('');
    setWebName('');
  };

  const handleClose = () => {
    if (!isBusy && !webLoading) {
      resetForm();
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (tab === 'device') {
      if (!file) {
        setError('Select a file to upload.');
        return;
      }
      onConfirm(file, name.trim());
      setFile(null);
      setName('');
      setError('');
      return;
    }
    if (tab === 'url') {
      const url = webUrl.trim();
      if (!url) {
        setError('Enter a URL to fetch.');
        return;
      }
      setWebLoading(true);
      setError('');
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') ?? '';
        const blob = await res.blob();
        if (!isSupportedMedia(blob, ct)) {
          throw new Error('URL must point to an image or video.');
        }
        const filename = webName.trim() || nameFromUrl(url);
        const fileFromUrl = new File([blob], filename, {
          type: blob.type || ct.split(';')[0],
        });
        onConfirm(fileFromUrl, filename);
        resetForm();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to fetch URL.');
      } finally {
        setWebLoading(false);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Asset"
      subtitle="Select a file from your computer to add to your library."
      width="max-w-md"
      footer={
        <div className="aspectly:flex aspectly:justify-end aspectly:gap-3 aspectly:w-full">
          <Button variant="ghost" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isBusy ||
              webLoading ||
              (tab === 'device' && !file) ||
              (tab === 'url' && webUrl.trim() === '') ||
              tab === 'drive'
            }
          >
            {isBusy ? 'Uploading...' : webLoading ? 'Fetching...' : 'Confirm'}
          </Button>
        </div>
      }
    >
      <div className="aspectly:space-y-6">
        <div className="aspectly:flex aspectly:p-1 aspectly:bg-surface-container-low aspectly:rounded-xl">
          <button
            className={`aspectly:flex-1 aspectly:py-2 aspectly:text-sm aspectly:font-bold aspectly:rounded-lg aspectly:transition-colors ${tab === 'device' ? 'aspectly:bg-surface-container-lowest aspectly:shadow aspectly:text-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'}`}
            onClick={() => {
              setTab('device');
              setError('');
            }}
          >
            From Device
          </button>
          <button
            className={`aspectly:flex-1 aspectly:py-2 aspectly:text-sm aspectly:font-bold aspectly:rounded-lg aspectly:transition-colors ${tab === 'url' ? 'aspectly:bg-surface-container-lowest aspectly:shadow aspectly:text-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'}`}
            onClick={() => {
              setTab('url');
              setError('');
            }}
          >
            Web URL
          </button>
          <button
            className={`aspectly:flex-1 aspectly:py-2 aspectly:text-sm aspectly:font-bold aspectly:rounded-lg aspectly:transition-colors ${tab === 'drive' ? 'aspectly:bg-surface-container-lowest aspectly:shadow aspectly:text-primary' : 'aspectly:text-on-surface-variant aspectly:hover:text-on-surface'}`}
            onClick={() => {
              setTab('drive');
              setError('');
            }}
          >
            Drive
          </button>
        </div>

        {tab === 'device' && (
          <div>
            <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
              File
            </label>
            <div className="aspectly:relative aspectly:border-2 aspectly:border-dashed aspectly:border-outline-variant/30 aspectly:rounded-xl aspectly:p-8 aspectly:hover:bg-surface-container-low aspectly:transition-colors aspectly:text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setFile(f);
                    if (!name) setName(f.name);
                    setError('');
                  }
                }}
                className="aspectly:absolute aspectly:inset-0 aspectly:w-full aspectly:h-full aspectly:opacity-0 aspectly:cursor-pointer aspectly:disabled:cursor-not-allowed"
                disabled={isBusy}
              />
              {file ? (
                <div className="aspectly:flex aspectly:flex-col aspectly:items-center">
                  <Icon name="check_circle" className="aspectly:text-4xl aspectly:text-primary aspectly:mb-2" />
                  <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface aspectly:truncate aspectly:max-w-full">
                    {file.name}
                  </p>
                  <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div className="aspectly:flex aspectly:flex-col aspectly:items-center">
                  <Icon name="upload_file" className="aspectly:text-4xl aspectly:text-on-surface-variant/50 aspectly:mb-2" />
                  <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface">Click or drag file to upload</p>
                  <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-1">
                    Supports Images/Videos up to 200MB
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'url' && (
          <div className="aspectly:space-y-4">
            <div>
              <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
                URL
              </label>
              <input
                type="url"
                required
                value={webUrl}
                onChange={(e) => setWebUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="aspectly:w-full aspectly:px-4 aspectly:py-3 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30 aspectly:transition-all aspectly:font-medium"
                disabled={isBusy || webLoading}
              />
            </div>
            <div>
              <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
                Display Name (Optional)
              </label>
              <input
                className="aspectly:w-full aspectly:px-4 aspectly:py-3 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30 aspectly:transition-all aspectly:font-medium"
                placeholder="e.g. Summer Collection Video"
                value={webName}
                onChange={(e) => setWebName(e.target.value)}
                disabled={isBusy || webLoading}
              />
            </div>
            <p className="aspectly:text-xs aspectly:text-on-surface-variant">
              We will fetch the URL and save the media to your asset library.
            </p>
          </div>
        )}

        {tab === 'drive' && (
          <div className="aspectly:border-2 aspectly:border-dashed aspectly:border-outline-variant/30 aspectly:rounded-xl aspectly:p-8 aspectly:flex aspectly:flex-col aspectly:items-center aspectly:justify-center aspectly:text-center">
            <Icon name="construction" className="aspectly:text-4xl aspectly:text-on-surface-variant/50 aspectly:mb-2" />
            <p className="aspectly:text-sm aspectly:font-bold aspectly:text-on-surface">Coming Soon</p>
            <p className="aspectly:text-xs aspectly:text-on-surface-variant aspectly:mt-1">
              This integration is currently under development.
            </p>
          </div>
        )}

        {tab === 'device' && (
          <div>
            <label className="aspectly:block aspectly:text-xs aspectly:font-semibold aspectly:text-on-surface-variant aspectly:uppercase aspectly:tracking-wider aspectly:mb-2">
              Display Name (Optional)
            </label>
            <input
              className="aspectly:w-full aspectly:px-4 aspectly:py-3 aspectly:bg-surface-container-low aspectly:text-sm aspectly:text-on-surface aspectly:border aspectly:border-outline-variant/20 aspectly:rounded-xl aspectly:focus:outline-hidden aspectly:focus:ring-2 aspectly:focus:ring-primary/30 aspectly:transition-all aspectly:font-medium"
              placeholder="e.g. Summer Collection Video"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isBusy || webLoading}
            />
          </div>
        )}
        {error && <p className="aspectly:text-xs aspectly:text-error aspectly:font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}
